"use client";

import { forwardRef, useMemo } from "react";
import {
  Label as AriaLabel,
  RadioGroup as AriaRadioGroup,
  FieldError,
} from "react-aria-components";

import { cn } from "../../utils/cn";
import { RadioProvider } from "./context";
import { Radio } from "./Radio";
import { radioVariants } from "./radioVariants";

import type {
  RadioGroupProps as AriaRadioGroupProps,
  ValidationResult,
} from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { RadioContext } from "./context";
import type { RadioClassNames } from "./radioVariants";

// RadioGroup - A radio group allows a user to select a single item from a list of mutually exclusive options.
//
// Structure:
// - <RadioGroup> - Container that manages selection state
// - <RadioGroup.Radio> - Individual radio option (requires unique `value`)
//
// Built on React Aria Components RadioGroup:
// https://react-spectrum.adobe.com/react-aria/RadioGroup.html

export interface RadioGroupProps
  extends Omit<AriaRadioGroupProps, "children">,
    VariantProps<typeof radioVariants> {
  children?: React.ReactNode;
  /** Label text for the radio group */
  label?: string;
  /** Error message to display when validation fails */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /** Custom class names for different slots */
  classNames?: RadioClassNames;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    { className, classNames, label, children, errorMessage, orientation = "vertical", ...props },
    ref
  ) => {
    const slots = useMemo(() => radioVariants({ orientation }), [orientation]);
    const context = useMemo<RadioContext>(() => ({ slots, classNames }), [slots, classNames]);

    return (
      <AriaRadioGroup
        {...props}
        ref={ref}
        orientation={orientation}
        className={cn(slots.group(), classNames?.group, className)}
      >
        {label && (
          <AriaLabel className={cn(slots.groupLabel(), classNames?.groupLabel)}>{label}</AriaLabel>
        )}
        <div className={cn(slots.radiosWrapper(), classNames?.radiosWrapper)}>
          <RadioProvider value={context}>{children}</RadioProvider>
        </div>
        <FieldError>{errorMessage}</FieldError>
      </AriaRadioGroup>
    );
  }
) as ForwardRefComponentWithSubcomponents<HTMLDivElement, { Radio: typeof Radio }, RadioGroupProps>;
RadioGroup.displayName = "RadioGroup";
RadioGroup.Radio = Radio;

export default RadioGroup;
export { Radio };
